/**
 * Phase 2B.3.5 — Super-admin SaaS insights dashboard.
 *
 * Visible only to the primary admin (Erika). Other agents see a 403.
 * Surfaces the four numbers that matter for theporchlight.io launch:
 *   - Signup volume (week + month)
 *   - Activation rate (signups who add ≥1 seller within 7d)
 - Subscription funnel (free listing / active / lapsed / grandfathered)
 - Free-listing-to-paid conversion
 * Plus a list of the 10 most recent signups for qualitative pulse-checking.
 */
import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Loader2, Users, TrendingUp, CheckCircle2, AlertCircle, ExternalLink, Activity, Mail } from "lucide-react";

export default function AdminInsights() {
  const [data, setData] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [trafficDays, setTrafficDays] = useState(30);
  // Sticky pref: whether to drop Gmail/Yahoo image-proxy prefetch opens
  // from the counters. Off by default so existing users don't see numbers
  // change without opt-in.
  const [excludePrefetch, setExcludePrefetch] = useState(() => {
    try { return localStorage.getItem("porchlight_exclude_prefetch") === "1"; }
    catch { return false; }
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/insights/saas");
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Marketing-site traffic loads independently so a 403 on the SaaS
  // endpoint doesn't hide it (and vice versa).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/insights/marketing?days=${trafficDays}&exclude_prefetch=${excludePrefetch ? "true" : "false"}`);
        if (!cancelled) setTraffic(data);
      } catch {
        // silent — primary-admin gate already handled by SaaS load above
      }
    })();
    return () => { cancelled = true; };
  }, [trafficDays, excludePrefetch]);

  // Persist the prefetch preference so opening the page tomorrow keeps
  // the admin's chosen view.
  useEffect(() => {
    try { localStorage.setItem("porchlight_exclude_prefetch", excludePrefetch ? "1" : "0"); }
    catch { /* private mode — silent */ }
  }, [excludePrefetch]);

  if (loading) {
    return (
      <div className="container-x" style={{ padding: "48px 0", display: "flex", alignItems: "center", justifyContent: "center", gap: 12, color: "var(--text-muted)" }}>
        <Loader2 size={18} className="spin" /> Loading insights…
      </div>
    );
  }
  if (err) {
    return (
      <div className="container-x" style={{ padding: "48px 0" }}>
        <div className="card" style={{ padding: 28, maxWidth: 540, display: "flex", gap: 14, alignItems: "flex-start" }}>
          <AlertCircle size={20} style={{ color: "var(--error)", flexShrink: 0, marginTop: 2 }} />
          <div>
            <h2 className="font-serif" style={{ fontSize: 22, fontWeight: 500, margin: 0, marginBottom: 6 }}>
              Insights are restricted
            </h2>
            <p data-testid="admin-insights-error" style={{ margin: 0, color: "var(--text-muted)", lineHeight: 1.55 }}>
              {err.includes("403") || err.toLowerCase().includes("not authorized")
                ? "This page is only visible to the primary PorchLight admin."
                : err}
            </p>
          </div>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const f = data.subscription_funnel || {};

  return (
    <div className="container-x" data-testid="admin-insights" style={{ padding: "48px 0 64px" }}>
      <div style={{ marginBottom: 32 }}>
        <span className="overline">Admin · Insights</span>
        <h1 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, margin: "8px 0 0", letterSpacing: "-0.01em" }}>
          PorchLight at a glance
        </h1>
        <p style={{ color: "var(--text-muted)", marginTop: 8 }}>
          Cross-tenant SaaS health. Updated when this page loads.
        </p>
      </div>

      {/* Top metrics row */}
      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", marginBottom: 28 }}>
        <MetricCard
          icon={<Users size={18} />}
          overline="Total agents"
          value={data.total_agents}
          testid="metric-total-agents"
        />
        <MetricCard
          icon={<TrendingUp size={18} />}
          overline="This week"
          value={data.weekly_signups}
          subtitle={`${data.monthly_signups} this month`}
          testid="metric-weekly-signups"
        />
        <MetricCard
          icon={<CheckCircle2 size={18} />}
          overline="Activation rate"
          value={data.activation_rate !== null ? `${data.activation_rate}%` : "—"}
          subtitle={data.activation_eligible
            ? `${data.activation_activated} of ${data.activation_eligible} signups added a seller within 7d`
            : "No agents past 7 days yet"}
          testid="metric-activation-rate"
          accent="#3F8B5A"
        />
        <MetricCard
          icon={<TrendingUp size={18} />}
          overline="Free to paid"
          value={data.free_to_paid_rate !== null ? `${data.free_to_paid_rate}%` : "—"}
          subtitle={data.free_to_paid_eligible
            ? `${data.free_to_paid_converted} of ${data.free_to_paid_eligible} converted`
            : "No one past their free listing yet"}
          testid="metric-trial-to-paid"
          accent="#9D4133"
        />
      </div>

      {/* Subscription funnel */}
      <div className="card" data-testid="subscription-funnel-card"
           style={{ padding: 28, marginBottom: 28 }}>
        <div className="overline">Subscription funnel</div>
        <h2 className="font-serif" style={{ fontSize: 22, fontWeight: 500, margin: "6px 0 18px", letterSpacing: "-0.005em" }}>
          Where every agent stands today
        </h2>
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
          <FunnelStat label="Free listing" value={f.trialing || 0} color="#5C4FAA" testid="funnel-trialing" />
          <FunnelStat label="Active (paid)" value={f.active || 0} color="#3F8B5A" testid="funnel-active" />
          <FunnelStat label="Lapsed" value={f.lapsed || 0} color="#B85C47" testid="funnel-lapsed" />
          <FunnelStat label="Grandfathered" value={f.grandfathered || 0} color="#78716C" testid="funnel-grandfathered" />
        </div>
      </div>

      {/* Marketing-site traffic — visitors hitting theporchlight.io / signup pages. */}
      <MarketingTrafficCard
        traffic={traffic}
        days={trafficDays}
        onChangeDays={setTrafficDays}
        excludePrefetch={excludePrefetch}
        setExcludePrefetch={setExcludePrefetch}
      />

      {/* Recent signups list */}
      <div className="card" data-testid="recent-signups-card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "22px 28px 16px" }}>
          <div className="overline">Last 10 signups</div>
          <h2 className="font-serif" style={{ fontSize: 22, fontWeight: 500, margin: "6px 0 0", letterSpacing: "-0.005em" }}>
            Newest agents
          </h2>
        </div>
        {(data.recent_signups || []).length === 0 ? (
          <p style={{ padding: "0 28px 24px", color: "var(--text-muted)", fontSize: 14 }}>
            No agent signups yet.
          </p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ background: "var(--surface-alt)", color: "var(--text-muted)", fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                <th style={cellHead}>Agent</th>
                <th style={cellHead}>Firm</th>
                <th style={cellHead}>Signed up</th>
                <th style={cellHead}>Status</th>
                <th style={cellHead}>Sellers (1st wk)</th>
                <th style={cellHead}>Portal</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_signups.map((s) => (
                <tr key={s.agent_slug} data-testid={`recent-signup-${s.agent_slug}`} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={cell}>
                    <div style={{ fontWeight: 500 }}>{s.name}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{s.email}</div>
                  </td>
                  <td style={cell}>{s.firm || <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                  <td style={cell}>{s.created_at ? new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}</td>
                  <td style={cell}>
                    <StatusPill status={s.subscription_status} />
                  </td>
                  <td style={cell}>
                    <span data-testid={`sellers-count-${s.agent_slug}`}
                          style={{ color: s.activated ? "#3F8B5A" : "var(--text-muted)", fontWeight: s.activated ? 500 : 400 }}>
                      {s.sellers_in_first_week}{s.activated ? " ✓" : ""}
                    </span>
                  </td>
                  <td style={cell}>
                    <a href={`/${s.agent_slug}/`} target="_blank" rel="noopener noreferrer"
                       data-testid={`open-portal-${s.agent_slug}`}
                       style={{ color: "var(--primary)", display: "inline-flex", alignItems: "center", gap: 4, textDecoration: "none", fontSize: 13 }}>
                      /{s.agent_slug} <ExternalLink size={12} />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const cellHead = { textAlign: "left", padding: "10px 28px", fontWeight: 500 };
const cell = { padding: "12px 28px", verticalAlign: "top" };

function MetricCard({ icon, overline, value, subtitle, accent, testid }) {
  return (
    <div className="card" data-testid={testid}
         style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, color: accent || "var(--text-muted)" }}>
        {icon}
        <span className="overline" style={{ margin: 0 }}>{overline}</span>
      </div>
      <div className="font-serif" style={{ fontSize: 36, fontWeight: 500, lineHeight: 1.05, letterSpacing: "-0.02em", color: accent || "var(--text)" }}>
        {value}
      </div>
      {subtitle && (
        <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>{subtitle}</div>
      )}
    </div>
  );
}

function FunnelStat({ label, value, color, testid }) {
  return (
    <div data-testid={testid} style={{
      padding: "16px 18px",
      borderRadius: 12,
      background: "var(--surface-alt)",
      borderLeft: `3px solid ${color}`,
    }}>
      <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 4 }}>
        {label}
      </div>
      <div className="font-serif" style={{ fontSize: 28, fontWeight: 500, lineHeight: 1, color }}>
        {value}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    trialing: { color: "#5C4FAA", label: "Free listing" },
    active: { color: "#3F8B5A", label: "Active" },
    lapsed: { color: "#B85C47", label: "Lapsed" },
  };
  const cfg = map[status] || { color: "var(--text-muted)", label: status || "—" };
  return (
    <span style={{
      display: "inline-block",
      padding: "3px 10px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 500,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      background: `${cfg.color}1A`,
      color: cfg.color,
    }}>{cfg.label}</span>
  );
}

function MarketingTrafficCard({ traffic, days, onChangeDays, excludePrefetch, setExcludePrefetch }) {
  const ranges = [7, 30, 90];
  const [showAllRecent, setShowAllRecent] = useState(false);
  if (!traffic) {
    return (
      <div className="card" data-testid="marketing-traffic-card-empty" style={{ padding: 28, marginBottom: 28 }}>
        <div className="overline">Marketing traffic</div>
        <p style={{ color: "var(--text-muted)", marginTop: 8, fontSize: 14 }}>
          No data yet. Once visitors land on theporchlight.io or /signup, traffic will appear here.
        </p>
      </div>
    );
  }
  const maxDayViews = Math.max(1, ...(traffic.views_by_day || []).map((d) => d.views));
  const refLabel = (k) => k || "Direct";
  const PAGE_NAMES = {
    "/": "Homepage",
    "/signup": "Signup page",
    "/for-agents": "Agents landing page",
  };
  const pageLabel = (p) => PAGE_NAMES[p] || p || "Homepage";
  const SOURCE_HINTS = {
    "Direct": "typed the address or used a bookmark",
    "Internal": "clicks from inside your own site",
    "Google": "found you on Google",
    "Google · Paid": "clicked a Google ad",
    "Search": "found you via a search engine",
    "Meta": "came from Facebook / Instagram",
    "Email": "clicked a link in an email",
    "Email · Launch broadcast": "clicked your launch email",
    "Email · Broadcast": "clicked one of your broadcast emails",
    "Email · Newsletter": "clicked your newsletter",
    "Other": "came from another website",
  };
  return (
    <div className="card" data-testid="marketing-traffic-card" style={{ padding: 28, marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
        <div>
          <div className="overline" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Activity size={14} /> Marketing traffic
          </div>
          <h2 className="font-serif" style={{ fontSize: 22, fontWeight: 500, margin: "6px 0 0", letterSpacing: "-0.005em" }}>
            theporchlight.io · last {traffic.window_days} days
          </h2>
          <p style={{ color: "var(--text-muted)", fontSize: 13, margin: "6px 0 0", maxWidth: 520 }}>
            A <b>click</b> is one page being opened. A <b>visitor</b> is one person
            (they can click several pages in a visit).
          </p>
        </div>
        <div style={{ display: "flex", gap: 6 }} data-testid="traffic-range-toggle">
          {ranges.map((d) => (
            <button
              key={d}
              type="button"
              data-testid={`traffic-range-${d}`}
              onClick={() => onChangeDays(d)}
              className={d === days ? "btn-primary" : "btn-ghost"}
              style={{ fontSize: 12, padding: "6px 12px" }}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", marginBottom: 22 }}>
        <SubMetric overline="Total clicks" value={traffic.total_views} subtitle="pages opened" testid="traffic-total-views" />
        <SubMetric overline="Visitors" value={traffic.unique_sessions} subtitle="individual people" testid="traffic-unique" />
        {traffic.email_opens > 0 && (
          <SubMetric
            overline="Email opens"
            value={traffic.email_opens}
            subtitle={
              excludePrefetch
                ? `${traffic.email_unique_openers || 0} unique · Gmail prefetch hidden`
                : `${traffic.email_unique_openers || 0} unique · Gmail may inflate`
            }
            testid="traffic-email-opens"
            accent="#2B4C3F"
          />
        )}
        <SubMetric overline="Signups" value={traffic.signups} testid="traffic-signups" accent="#3F8B5A" />
        <SubMetric
          overline="Conversion"
          value={traffic.unique_sessions ? `${traffic.conversion_rate}%` : "—"}
          subtitle="Signups / unique"
          testid="traffic-conversion"
          accent="#9D4133"
        />
      </div>

      {/* Gmail prefetch toggle — only surfaced when there are prefetches to
          hide. Sticky via localStorage. Clicking refetches the endpoint
          with `exclude_prefetch=true|false` so opens + A/B numbers update. */}
      {traffic.email_prefetch_available > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22, flexWrap: "wrap" }}>
          <button
            type="button"
            data-testid="traffic-prefetch-toggle"
            onClick={() => setExcludePrefetch((v) => !v)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "6px 14px", borderRadius: 999,
              fontSize: 12, fontWeight: 600, letterSpacing: "0.02em",
              cursor: "pointer",
              background: excludePrefetch ? "var(--primary)" : "var(--surface-alt)",
              color: excludePrefetch ? "#fff" : "var(--text)",
              border: `1px solid ${excludePrefetch ? "var(--primary)" : "var(--border)"}`,
              transition: "all 120ms ease",
            }}
          >
            <span style={{
              width: 8, height: 8, borderRadius: 999,
              background: excludePrefetch ? "#fff" : "var(--text-faint)",
            }} />
            {excludePrefetch ? "Gmail prefetch hidden" : "Show all opens (incl. Gmail prefetch)"}
          </button>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            {excludePrefetch
              ? <>Hiding <b data-testid="traffic-prefetch-hidden-count">{traffic.email_prefetch_hidden}</b> Gmail/Yahoo image-proxy open{traffic.email_prefetch_hidden === 1 ? "" : "s"}.</>
              : <><b data-testid="traffic-prefetch-available-count">{traffic.email_prefetch_available}</b> of your opens came from Gmail&rsquo;s image proxy (Google prefetches images before you actually open the email).</>
            }
          </span>
        </div>
      )}

      {/* Email click callout — only shown if any email-tagged visit exists. */}
      {traffic.email_views > 0 && (
        <div data-testid="traffic-email-callout" style={{
          padding: "16px 20px", marginBottom: 22,
          background: "linear-gradient(135deg, rgba(43,76,63,0.06), rgba(157,65,51,0.04))",
          border: "1px solid rgba(43,76,63,0.18)", borderRadius: 12,
          display: "flex", flexWrap: "wrap", alignItems: "center", gap: 18,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 200 }}>
            <Mail size={20} color="var(--primary)" />
            <div>
              <div className="overline" style={{ margin: 0 }}>From your emails</div>
              <div className="font-serif" style={{ fontSize: 22, fontWeight: 500, lineHeight: 1.1 }}>
                {traffic.email_views} click{traffic.email_views === 1 ? "" : "s"}
                <span style={{ fontSize: 13, color: "var(--text-muted)", fontFamily: "inherit", fontWeight: 400, marginLeft: 6 }}>
                  · {traffic.email_unique} visitor{traffic.email_unique === 1 ? "" : "s"}
                </span>
              </div>
            </div>
          </div>
          {traffic.email_campaigns?.length > 0 && (
            <div style={{ flex: 1, minWidth: 240, display: "flex", flexDirection: "column", gap: 4 }}>
              {traffic.email_campaigns.map((c) => (
                <div key={c.campaign} data-testid={`traffic-email-campaign-${c.campaign}`} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ color: "var(--text-muted)" }}>
                    Campaign: <code style={{ fontSize: 12 }}>{c.campaign}</code>
                  </span>
                  <span>{c.views} click{c.views === 1 ? "" : "s"} <span style={{ color: "var(--text-muted)" }}>· {c.unique} visitor{c.unique === 1 ? "" : "s"}</span></span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* A/B test results — one card per campaign where the send used
          subject_a / subject_b tagging. Shows opens + unique + clicks +
          ctr side-by-side plus a "winner" badge. */}
      {(traffic.ab_results || []).length > 0 && (
        <div data-testid="traffic-ab-results" style={{ marginBottom: 24 }}>
          <div className="overline" style={{ marginBottom: 10 }}>A/B subject-line results</div>
          <div style={{ display: "grid", gap: 12 }}>
            {traffic.ab_results.map((r) => (
              <ABCard key={r.campaign} row={r} />
            ))}
          </div>
          <p style={{ fontSize: 11, color: "var(--text-faint)", margin: "8px 2px 0", lineHeight: 1.5 }}>
            Winner is decided by <b>unique opens</b> first (raw opens are inflated by Gmail&rsquo;s image prefetch),
            then <b>unique clicks</b> as a tiebreaker. CTR = clicks ÷ opens.
          </p>
        </div>
      )}

      {/* Who clicked — per-recipient email attribution (broadcasts sent after
          link personalization shipped carry a pl_r token). */}
      {traffic.email_views > 0 && (
        <div data-testid="traffic-email-clickers" style={{ marginBottom: 24 }}>
          <div className="overline" style={{ marginBottom: 4 }}>Who clicked your emails</div>
          {(traffic.email_clickers || []).length === 0 ? (
            <p style={{ color: "var(--text-muted)", fontSize: 12, margin: 0 }}>
              No named clicks yet. Emails sent from now on tag each recipient&rsquo;s links,
              so future clicks will show exactly who opened them. Older sends stay anonymous.
            </p>
          ) : (
            <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
              {traffic.email_clickers.map((c, i, arr) => (
                <div key={c.email} data-testid={`traffic-clicker-${c.email}`}
                     style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "4px 14px",
                              padding: "10px 16px", fontSize: 13,
                              borderBottom: i < arr.length - 1 ? "1px solid var(--border)" : "none" }}>
                  <span style={{ fontWeight: 600, color: "var(--primary)" }}>{c.email}</span>
                  <span style={{ color: "var(--text-muted)" }}>
                    {c.clicks} click{c.clicks === 1 ? "" : "s"}
                    {c.campaigns?.length ? ` · ${c.campaigns.join(", ")}` : ""}
                  </span>
                  <span style={{ color: "var(--text-muted)", marginLeft: "auto", whiteSpace: "nowrap" }}>
                    last: {fmtVisitDateTime(c.last_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Clicks by date — plain rows, newest first, no hovering required. */}
      {traffic.views_by_day?.length > 0 && (
        <div data-testid="traffic-clicks-by-date" style={{ marginBottom: 24 }}>
          <div className="overline" style={{ marginBottom: 10 }}>Clicks by date</div>
          <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
            {[...traffic.views_by_day].reverse().map((d, i, arr) => {
              const w = Math.max(3, Math.round((d.views / maxDayViews) * 100));
              return (
                <div key={d.date} data-testid={`traffic-day-${d.date}`}
                     style={{ display: "flex", alignItems: "center", gap: 14, padding: "9px 16px",
                              borderBottom: i < arr.length - 1 ? "1px solid var(--border)" : "none" }}>
                  <span style={{ width: 92, flexShrink: 0, fontSize: 13, fontWeight: 500 }}>
                    {fmtVisitDate(d.date + "T12:00:00Z")}
                  </span>
                  <div style={{ flex: 1, height: 10, background: "var(--surface-alt)", borderRadius: 999, overflow: "hidden" }}>
                    <div style={{ width: `${w}%`, height: "100%", background: "linear-gradient(90deg, #2B4C3F, #5A7C6E)", borderRadius: 999 }} />
                  </div>
                  <span style={{ width: 150, flexShrink: 0, textAlign: "right", fontSize: 13 }}>
                    <b>{d.views}</b> click{d.views === 1 ? "" : "s"}
                    <span style={{ color: "var(--text-muted)" }}> · {d.unique} visitor{d.unique === 1 ? "" : "s"}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Two columns: top referrers + top paths */}
      <div style={{ display: "grid", gap: 24, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
        <div data-testid="traffic-referrers">
          <div className="overline" style={{ marginBottom: 4 }}>Where visitors came from</div>
          <p style={{ color: "var(--text-muted)", fontSize: 12, margin: "0 0 10px" }}>How people found your site.</p>
          {(traffic.top_referrers || []).length === 0 ? (
            <div style={{ color: "var(--text-muted)", fontSize: 13 }}>No traffic yet.</div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
              {traffic.top_referrers.map((r) => (
                <li key={r.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, padding: "8px 0", borderBottom: "1px solid var(--border)", fontSize: 14 }}>
                  <span style={{ minWidth: 0 }}>
                    {refLabel(r.key)}
                    <span data-testid={`traffic-referrer-last-${r.key}`} style={{ display: "block", fontSize: 11, color: "var(--text-muted)" }}>
                      {SOURCE_HINTS[r.key] ? `${SOURCE_HINTS[r.key]} · ` : ""}last visit {fmtVisitDate(r.last_seen)}
                    </span>
                  </span>
                  <span style={{ color: "var(--text-muted)", whiteSpace: "nowrap" }}><b style={{ color: "var(--text)" }}>{r.views}</b> click{r.views === 1 ? "" : "s"} · {r.unique} visitor{r.unique === 1 ? "" : "s"}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div data-testid="traffic-paths">
          <div className="overline" style={{ marginBottom: 4 }}>Which pages they opened</div>
          <p style={{ color: "var(--text-muted)", fontSize: 12, margin: "0 0 10px" }}>What visitors looked at once they arrived.</p>
          {(traffic.top_paths || []).length === 0 ? (
            <div style={{ color: "var(--text-muted)", fontSize: 13 }}>No traffic yet.</div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
              {traffic.top_paths.map((p) => (
                <li key={p.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, padding: "8px 0", borderBottom: "1px solid var(--border)", fontSize: 14 }}>
                  <span style={{ minWidth: 0 }}>
                    {pageLabel(p.key)}
                    <span data-testid={`traffic-path-last-${p.key}`} style={{ display: "block", fontSize: 11, color: "var(--text-muted)" }}>
                      <code style={{ fontSize: 11 }}>{p.key}</code> · last visit {fmtVisitDate(p.last_seen)}
                    </span>
                  </span>
                  <span style={{ color: "var(--text-muted)", whiteSpace: "nowrap" }}><b style={{ color: "var(--text)" }}>{p.views}</b> click{p.views === 1 ? "" : "s"} · {p.unique} visitor{p.unique === 1 ? "" : "s"}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Recent clicks — every visit with its exact date & time, newest first. */}
      {(traffic.recent_visits || []).length > 0 && (
        <div data-testid="traffic-recent-clicks" style={{ marginTop: 28 }}>
          <div className="overline" style={{ marginBottom: 4 }}>Latest clicks</div>
          <p style={{ color: "var(--text-muted)", fontSize: 12, margin: "0 0 10px" }}>The most recent visits, one row per page opened.</p>
          <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
            {(showAllRecent ? traffic.recent_visits : traffic.recent_visits.slice(0, 8)).map((v, i, arr) => (
              <div key={`${v.at}-${i}`} data-testid={`traffic-recent-row-${i}`}
                   style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "4px 14px",
                            padding: "10px 16px", fontSize: 13,
                            borderBottom: i < arr.length - 1 ? "1px solid var(--border)" : "none",
                            background: i % 2 ? "var(--surface-alt)" : "transparent" }}>
                <span style={{ fontWeight: 500, whiteSpace: "nowrap", minWidth: 150 }}>{fmtVisitDateTime(v.at)}</span>
                <span>{pageLabel(v.path)}</span>
                {v.recipient && (
                  <span data-testid={`traffic-recent-recipient-${i}`} style={{ fontWeight: 600, color: "var(--primary)" }}>
                    {v.recipient}
                  </span>
                )}
                <span style={{ color: "var(--text-muted)", marginLeft: "auto", whiteSpace: "nowrap" }}>
                  via {refLabel(v.source)}
                  {v.medium === "email" && v.campaign ? ` (campaign: ${v.campaign})` : ""}
                </span>
              </div>
            ))}
          </div>
          {traffic.recent_visits.length > 8 && (
            <button
              type="button"
              data-testid="traffic-recent-toggle"
              className="btn-ghost"
              style={{ fontSize: 12, padding: "6px 12px", marginTop: 8 }}
              onClick={() => setShowAllRecent((s) => !s)}
            >
              {showAllRecent ? "Show fewer" : `Show all ${traffic.recent_visits.length}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function fmtVisitDate(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function fmtVisitDateTime(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}


function ABCard({ row }) {
  const { campaign, a, b, winner } = row;
  const pill = (variant, label) => {
    const isWinner = winner === variant;
    return (
      <span
        data-testid={`ab-${campaign}-${variant}-badge`}
        style={{
          display: "inline-block",
          padding: "2px 10px",
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          background: isWinner ? "var(--primary)" : "var(--surface-alt)",
          color: isWinner ? "#fff" : "var(--text-muted)",
          border: isWinner ? "1px solid var(--primary)" : "1px solid var(--border)",
        }}
      >
        {label}{isWinner ? " · winner" : ""}
      </span>
    );
  };
  const col = (variant, v) => (
    <div data-testid={`ab-${campaign}-${variant}-col`} style={{ flex: 1, minWidth: 180 }}>
      <div style={{ marginBottom: 8 }}>{pill(variant, `Variant ${variant.toUpperCase()}`)}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
        <div>
          <b data-testid={`ab-${campaign}-${variant}-opens`}>{v.opens}</b>
          <span style={{ color: "var(--text-muted)" }}> open{v.opens === 1 ? "" : "s"} · {v.unique_opens} unique</span>
        </div>
        <div>
          <b data-testid={`ab-${campaign}-${variant}-clicks`}>{v.clicks}</b>
          <span style={{ color: "var(--text-muted)" }}> click{v.clicks === 1 ? "" : "s"} · {v.unique_clicks} unique</span>
        </div>
        <div style={{ color: "var(--text-muted)" }}>
          CTR: <b style={{ color: "var(--text)" }}>{v.ctr}%</b>
        </div>
      </div>
    </div>
  );
  return (
    <div
      data-testid={`ab-card-${campaign}`}
      style={{
        border: "1px solid var(--border)",
        borderRadius: 12,
        padding: "14px 16px",
        background: "#fff",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <span className="overline" style={{ margin: 0 }}>Campaign</span>
        <code style={{ fontSize: 13, color: "var(--text)" }}>{campaign}</code>
        {winner === "tie" && (
          <span style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 700 }}>
            · tied so far
          </span>
        )}
      </div>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
        {col("a", a)}
        {col("b", b)}
      </div>
    </div>
  );
}


function SubMetric({ overline, value, subtitle, accent, testid }) {
  return (
    <div data-testid={testid} style={{ padding: "12px 16px", background: "var(--surface-alt)", borderRadius: 10 }}>
      <div className="overline" style={{ color: accent || "var(--text-muted)", margin: 0 }}>{overline}</div>
      <div className="font-serif" style={{ fontSize: 24, fontWeight: 500, lineHeight: 1.1, marginTop: 4, color: accent || "var(--text)" }}>
        {value}
      </div>
      {subtitle && <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{subtitle}</div>}
    </div>
  );
}


